import { defineStore } from 'pinia';
import vuexStore from 'dashboard/store';
import { fetchBrowserPhoneBootstrap } from '../api/bootstrap';
import {
  createBrowserPhoneCdr,
  uploadBrowserPhoneRecording,
} from '../api/cdr';
import { normalizeBrowserPhoneBootstrap } from '../utils/bootstrapMapper';
import { sanitizeDialNumber } from '../utils/number';
import { prepareBrowserPhoneRecordingUpload } from '../utils/recording';
import { browserPhoneEngine } from '../core/engine';
import { buildBrowserPhoneCdr } from '../core/cdrBuilder';
import {
  clearWidgetState,
  readWidgetState,
  writeWidgetState,
} from '../core/storage';

const persistedState = readWidgetState();
let engineBound = false;

const accountIdFromStore = () => Number(vuexStore.getters.getCurrentAccountId);
const currentUserFromStore = () => vuexStore.getters.getCurrentUser || {};
const currentUserIdFromStore = () => currentUserFromStore().id || null;
const currentUserNameFromStore = () => currentUserFromStore().name || null;

const buildDialContext = payload => ({
  accountId: accountIdFromStore(),
  agentId: currentUserIdFromStore(),
  agentName: currentUserNameFromStore(),
  contactId: payload.contactId || null,
  conversationId: payload.conversationId || null,
  contactName: payload.contactName || null,
  number: payload.number || null,
  source: payload.source || 'widget',
});

const emitPhoneCdrUpserted = cdr => {
  window.dispatchEvent(
    new CustomEvent('phone-cdr-upserted', {
      detail: { cdr },
    })
  );
};

const disabledBootstrapReason = payload => {
  if (!payload || payload.enabled) return null;
  return payload.reason || 'disabled';
};

export const useBrowserPhoneStore = defineStore('browserPhone', {
  state: () => ({
    bootstrapPayload: null,
    isBootstrapping: false,
    isEnabled: false,
    isWidgetOpen: persistedState.isWidgetOpen || false,
    isRuntimeReady: false,
    registrationStatus: 'idle',
    registrationError: null,
    runtimeRootAttached: false,
    dialInput: '',
    showSettings: false,
    calls: [],
    activeCallId: null,
    pendingDialRequest: null,
    pendingDialContext: null,
    dialContextsByNumber: {},
    lastCdrError: null,
  }),

  getters: {
    currentCall(state) {
      if (state.activeCallId) {
        return state.calls.find(call => call.id === state.activeCallId) || null;
      }

      return state.calls[0] || null;
    },
    incomingCall(state) {
      return state.calls.find(call => call.status === 'incoming') || null;
    },
  },

  actions: {
    emitPhoneCdrUpserted(cdr) {
      emitPhoneCdrUpserted(cdr);
    },

    async uploadRecordingsForCdr(phoneCdr, cdr, accountId) {
      const recordings = Array.isArray(cdr?.Recordings) ? cdr.Recordings : [];
      if (!phoneCdr?.id || !recordings.length) {
        return phoneCdr;
      }

      let persistedPhoneCdr = phoneCdr;

      for (const recording of recordings) {
        if (!recording?.uID) {
          continue;
        }

        try {
          // eslint-disable-next-line no-await-in-loop
          const blob = await browserPhoneEngine.getRecordingBlob(recording.uID);
          if (!blob?.size) {
            continue;
          }

          // eslint-disable-next-line no-await-in-loop
          const preparedUpload = await prepareBrowserPhoneRecordingUpload(
            cdr,
            recording.uID,
            blob
          );

          // eslint-disable-next-line no-await-in-loop
          persistedPhoneCdr = await uploadBrowserPhoneRecording({
            accountId,
            file: preparedUpload.blob,
            filename: preparedUpload.filename,
            phoneCdrId: persistedPhoneCdr.id,
            recordingUid: recording.uID,
          });

          if (persistedPhoneCdr?.cdr_json) {
            this.emitPhoneCdrUpserted(persistedPhoneCdr.cdr_json);
          }
        } catch (error) {
          this.lastCdrError = error;
        }
      }

      return persistedPhoneCdr;
    },

    async persistBrowserPhoneCdr(cdr, context = null) {
      const accountId = context?.accountId || accountIdFromStore();
      const cdrContext = {
        accountId,
        agentId: currentUserIdFromStore(),
        agentName: currentUserNameFromStore(),
        ...(context || {}),
      };
      const persistedPhoneCdr = await createBrowserPhoneCdr(
        buildBrowserPhoneCdr(cdr, cdrContext),
        accountId
      );

      if (persistedPhoneCdr?.cdr_json) {
        this.emitPhoneCdrUpserted(persistedPhoneCdr.cdr_json);
      }

      return this.uploadRecordingsForCdr(persistedPhoneCdr, cdr, accountId);
    },

    bindEngine() {
      if (engineBound) return;

      engineBound = true;

      browserPhoneEngine.on('widget:show', () => {
        this.isWidgetOpen = true;
        this.persistWidgetState();
      });

      browserPhoneEngine.on('runtime:initialized', () => {
        this.isRuntimeReady = true;
        this.registrationStatus = 'loading';
      });

      browserPhoneEngine.on('registration:changed', payload => {
        this.registrationStatus = payload.status;
        if (payload.status === 'registered') {
          this.registrationError = null;
        } else if (payload.status === 'unregistered') {
          this.registrationError = null;
        } else if (payload.error) {
          this.registrationError = payload.error;
        }

        if (payload.status === 'registered' && this.pendingDialRequest) {
          const request = this.pendingDialRequest;
          this.pendingDialRequest = null;
          browserPhoneEngine.dial(request.number);
        }
      });

      browserPhoneEngine.on('call:updated', call => {
        this.upsertCall(call);
        this.activeCallId = call.id;
        this.isWidgetOpen = true;
        this.showSettings = false;
        this.persistWidgetState();

        if (
          call.direction === 'outbound' &&
          call.number &&
          this.pendingDialContext
        ) {
          this.dialContextsByNumber = {
            ...this.dialContextsByNumber,
            [call.number]: this.pendingDialContext,
          };
          this.pendingDialContext = null;
        }
      });

      browserPhoneEngine.on('call:ended', call => {
        this.calls = this.calls.filter(existingCall => existingCall.id !== call.id);
        this.activeCallId = this.calls[0]?.id || null;
      });

      browserPhoneEngine.on('cdr:created', async cdr => {
        const context =
          cdr.CallDirection === 'outbound'
            ? this.dialContextsByNumber[cdr.DstUserId] || null
            : null;

        try {
          await this.persistBrowserPhoneCdr(cdr, context);
        } catch (error) {
          this.lastCdrError = error;
        } finally {
          if (cdr.CallDirection === 'outbound' && cdr.DstUserId) {
            const nextContexts = { ...this.dialContextsByNumber };
            delete nextContexts[cdr.DstUserId];
            this.dialContextsByNumber = nextContexts;
          }
        }
      });
    },

    attachRuntimeRoot(element) {
      if (!element) return;

      browserPhoneEngine.attachRoot(element);
      this.runtimeRootAttached = true;

      if (this.bootstrapPayload?.enabled && !this.isRuntimeReady) {
        if (browserPhoneEngine.isLoaded()) {
          browserPhoneEngine.setBootstrapOptions(this.bootstrapPayload);
          this.isRuntimeReady = true;
          this.registrationStatus = 'loading';
          browserPhoneEngine.register();
          return;
        }

        browserPhoneEngine.initialize(this.bootstrapPayload).catch(error => {
          this.registrationStatus = 'failed';
          this.registrationError = error?.message || String(error);
        });
      }
    },

    async bootstrap() {
      if (this.isBootstrapping) return;

      const accountId = accountIdFromStore();
      if (!accountId) return;

      this.bindEngine();
      this.isBootstrapping = true;
      this.registrationError = null;
      this.registrationStatus = this.bootstrapPayload ? this.registrationStatus : 'loading';

      try {
        const payload = normalizeBrowserPhoneBootstrap(
          await fetchBrowserPhoneBootstrap(accountId)
        );

        this.bootstrapPayload = payload;
        this.isEnabled = payload.enabled;
        this.dialInput = this.dialInput || '';

        if (!payload.enabled) {
          this.pendingDialRequest = null;
          this.pendingDialContext = null;
          this.registrationError = null;
          this.registrationStatus = 'idle';
          browserPhoneEngine.unregister();
          browserPhoneEngine.clearPhoneOptions();
          browserPhoneEngine.clearTransientUi();
          return;
        }

        if (payload.enabled && this.runtimeRootAttached && !this.isRuntimeReady) {
          if (browserPhoneEngine.isLoaded()) {
            browserPhoneEngine.setBootstrapOptions(payload);
            this.isRuntimeReady = true;
            this.registrationStatus = 'loading';
            browserPhoneEngine.register();
            return;
          }

          await browserPhoneEngine.initialize(payload);
        }
      } catch (error) {
        this.registrationStatus = 'failed';
        this.registrationError = error?.message || String(error);
      } finally {
        this.isBootstrapping = false;
      }
    },

    resetState({ clearPersistedWidgetState = false } = {}) {
      this.bootstrapPayload = null;
      this.isBootstrapping = false;
      this.isEnabled = false;
      this.isWidgetOpen = false;
      this.isRuntimeReady = false;
      this.registrationStatus = 'idle';
      this.registrationError = null;
      this.dialInput = '';
      this.showSettings = false;
      this.calls = [];
      this.activeCallId = null;
      this.pendingDialRequest = null;
      this.pendingDialContext = null;
      this.dialContextsByNumber = {};
      this.lastCdrError = null;

      browserPhoneEngine.clearPhoneOptions();
      browserPhoneEngine.clearTransientUi();

      if (clearPersistedWidgetState) {
        clearWidgetState();
        return;
      }

      this.persistWidgetState();
    },

    teardown({ clearPersistedWidgetState = true } = {}) {
      browserPhoneEngine.unregister();
      this.resetState({ clearPersistedWidgetState });
      this.runtimeRootAttached = false;
    },

    async handleAccountChange() {
      browserPhoneEngine.unregister();
      this.resetState({ clearPersistedWidgetState: true });

      const accountId = accountIdFromStore();
      if (!accountId || !this.runtimeRootAttached) {
        return;
      }

      await this.bootstrap();
    },

    async retryRegistration() {
      if (this.isBootstrapping) return;

      if (!this.bootstrapPayload) {
        await this.bootstrap();
      }

      if (!this.bootstrapPayload?.enabled) {
        if (disabledBootstrapReason(this.bootstrapPayload)) {
          await this.bootstrap();
        }
        return;
      }

      if (!this.runtimeRootAttached || !this.isRuntimeReady) {
        await this.bootstrap();
        return;
      }

      browserPhoneEngine.register();
    },

    persistWidgetState() {
      writeWidgetState({
        isWidgetOpen: this.isWidgetOpen,
      });
    },

    upsertCall(call) {
      const existingIndex = this.calls.findIndex(
        existingCall => existingCall.id === call.id
      );

      if (existingIndex === -1) {
        this.calls = [call, ...this.calls];
        return;
      }

      this.calls = this.calls.map(existingCall =>
        existingCall.id === call.id ? { ...existingCall, ...call } : existingCall
      );
    },

    open(payload = {}) {
      if (!this.bootstrapPayload && !this.isBootstrapping) {
        this.bootstrap();
      }

      this.isWidgetOpen = true;
      this.showSettings = false;
      this.persistWidgetState();

      if (payload.number) {
        this.dialInput = payload.number;
      }

      if (payload.source) {
        this.pendingDialContext = buildDialContext(payload);
      }
    },

    close() {
      this.isWidgetOpen = false;
      this.persistWidgetState();
    },

    setDialInput(value) {
      this.dialInput = value;
    },

    appendDigit(digit) {
      this.dialInput = `${this.dialInput}${digit}`;
    },

    removeLastDigit() {
      this.dialInput = this.dialInput.slice(0, -1);
    },

    toggleSettings() {
      this.showSettings = !this.showSettings;
    },

    async dial(payload = {}) {
      await this.bootstrap();

      const number = sanitizeDialNumber(payload.number || this.dialInput);
      if (!number || !this.bootstrapPayload?.enabled) return;

      this.open({ ...payload, number });
      this.dialInput = number;
      this.pendingDialContext = buildDialContext({ ...payload, number });

      if (this.registrationStatus === 'registered') {
        browserPhoneEngine.dial(number);
        return;
      }

      this.pendingDialRequest = { number };
    },

    answer(callId) {
      if (!callId) return;
      this.isWidgetOpen = true;
      browserPhoneEngine.answer(callId);
    },

    reject(callId) {
      if (!callId) return;
      browserPhoneEngine.reject(callId);
    },

    hold(callId) {
      if (!callId) return;
      browserPhoneEngine.hold(callId);
    },

    resume(callId) {
      if (!callId) return;
      browserPhoneEngine.resume(callId);
    },

    mute(callId) {
      if (!callId) return;
      browserPhoneEngine.mute(callId);
    },

    unmute(callId) {
      if (!callId) return;
      browserPhoneEngine.unmute(callId);
    },

    hangup(callId) {
      if (!callId) return;
      browserPhoneEngine.hangup(callId);
    },

    sendDtmf({ callId, tone }) {
      if (!callId || !tone) return;
      browserPhoneEngine.sendDtmf(callId, tone);
    },

    startRecording(callId) {
      if (!callId) return;
      browserPhoneEngine.startRecording(callId);
    },

    stopRecording(callId) {
      if (!callId) return;
      browserPhoneEngine.stopRecording(callId);
    },

    getTransferState(callId) {
      if (!callId) return browserPhoneEngine.getTransferState(null);
      return browserPhoneEngine.getTransferState(callId);
    },

    getConferenceState(callId) {
      if (!callId) return browserPhoneEngine.getConferenceState(null);
      return browserPhoneEngine.getConferenceState(callId);
    },

    blindTransfer({ callId, number }) {
      const sanitizedNumber = sanitizeDialNumber(number);
      if (!callId || !sanitizedNumber) return false;

      return browserPhoneEngine.blindTransfer(callId, sanitizedNumber);
    },

    startAttendedTransfer({ callId, number }) {
      const sanitizedNumber = sanitizeDialNumber(number);
      if (!callId || !sanitizedNumber) return false;

      return browserPhoneEngine.startAttendedTransfer(callId, sanitizedNumber);
    },

    completeAttendedTransfer(callId) {
      if (!callId) return false;

      return browserPhoneEngine.completeAttendedTransfer(callId);
    },

    cancelTransfer(callId) {
      if (!callId) return false;

      return browserPhoneEngine.cancelTransfer(callId);
    },

    endAttendedTransfer(callId) {
      if (!callId) return false;

      return browserPhoneEngine.endAttendedTransfer(callId);
    },

    startConference({ callId, number }) {
      const sanitizedNumber = sanitizeDialNumber(number);
      if (!callId || !sanitizedNumber) return false;

      return browserPhoneEngine.startConference(callId, sanitizedNumber);
    },

    joinConference(callId) {
      if (!callId) return false;

      return browserPhoneEngine.joinConference(callId);
    },

    cancelConference(callId) {
      if (!callId) return false;

      return browserPhoneEngine.cancelConference(callId);
    },

    endConference(callId) {
      if (!callId) return false;

      return browserPhoneEngine.endConference(callId);
    },
  },
});
